import React, { useState } from 'react';
import 'twin.macro';
import { ButtonDisplay } from 'components/Buttons/ButtonDisplay';
import _uniq from 'utils/uniq';
import Button from 'components/Buttons/Button';
import { CheckIcon } from '@heroicons/react/24/outline';
import Input from 'components/Form/Input2';
import { useTranslation } from 'react-i18next';

export interface ChipOption<T = string | number> {
  value: T;
  label: string;
}

export interface ChipsProps<T = string | number> {
  options: ChipOption<T>[];
  value: T[];
  onChange: (value: T[]) => void;
  className?: string;
  size?: 'md' | 'lg';
  withCustom?: boolean;
}

export function Chips<T = string | number>({
  options,
  value,
  onChange,
  className,
  size = 'lg',
  withCustom,
}: ChipsProps<T>) {
  function handleToggle(optionValue: T) {
    if (value.includes(optionValue)) {
      onChange(_uniq(value.filter(v => v !== optionValue).sort()));
      return;
    }
    onChange(_uniq([...value, optionValue]).sort());
  }
  const [showCustom, setShowCustom] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const { t } = useTranslation(['common']);

  return (
    <div tw="flex flex-wrap gap-2" className={className}>
      {options.map(option => (
        <ButtonDisplay
          size={size}
          key={String(option.value)}
          onClick={() => handleToggle(option.value)}
          active={value.includes(option.value)}
          data-testid={`chip-${String(option.value)}`}
        >
          {option.label}
        </ButtonDisplay>
      ))}
      {withCustom && !showCustom && (
        <Button minimal small onClick={() => setShowCustom(true)}>
          {t('common:buttons.add')}
        </Button>
      )}
      {withCustom && showCustom && (
        <div tw="flex items-center gap-2">
          <Input
            value={customValue}
            onChange={e => setCustomValue(e.target.value)}
            tw="-my-1"
          />
          <Button
            minimal
            small
            onClick={() => {
              handleToggle(customValue as T);
              setCustomValue('');
              setShowCustom(false);
            }}
            icon={CheckIcon}
            disabled={!customValue.trim()}
          />
        </div>
      )}
    </div>
  );
}
